import React, { useEffect, useMemo, useState } from 'react';
import { settingsApi } from '../utils/api';
import { CheckCircle2, Copy, Mail, Send, UserPlus, X } from 'lucide-react';

type Role = 'ADMIN' | 'OFFICE' | 'ENGINEER';

interface Props {
  open: boolean;
  defaultRole?: Role;
  onClose: () => void;
  onSent?: () => void;
  showToast?: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
}

const ROLE_META: Record<Role, { label: string; blurb: string }> = {
  ENGINEER: {
    label: 'Engineer',
    blurb: 'Uses the mobile app. Sets a 6-digit PIN on first sign in.',
  },
  OFFICE: {
    label: 'Office',
    blurb: 'Coordinates jobs, quotes, invoices from the web dashboard.',
  },
  ADMIN: {
    label: 'Admin',
    blurb: 'Full access including team, billing and company settings.',
  },
};

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const InviteMemberModal: React.FC<Props> = ({
  open,
  defaultRole = 'ENGINEER',
  onClose,
  onSent,
  showToast,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>(defaultRole);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<null | {
    email: string;
    acceptUrl: string;
    emailDelivered: boolean;
  }>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setRole(defaultRole);
    setError(null);
    setResult(null);
    setCopied(false);
    setSubmitting(false);
  }, [open, defaultRole]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const canSubmit = useMemo(
    () => !submitting && isEmail(email),
    [submitting, email],
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res: any = await settingsApi.inviteMember({
        email: email.trim(),
        role,
      });
      const data = res?.data || res;
      const acceptUrl: string = data?.acceptUrl || '';
      const emailDelivered: boolean = data?.emailDelivered !== false;
      setResult({ email: email.trim(), acceptUrl, emailDelivered });
      onSent?.();
      showToast?.(
        emailDelivered ? 'Invite sent' : 'Invite created — email may have failed',
        emailDelivered ? 'success' : 'info',
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not send invite. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!result?.acceptUrl) return;
    try {
      await navigator.clipboard.writeText(result.acceptUrl);
      setCopied(true);
      showToast?.('Invite link copied', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast?.('Could not copy to clipboard', 'error');
    }
  };

  const inviteAnother = () => {
    setResult(null);
    setEmail('');
    setError(null);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E6E4]">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-orange-500 text-white flex items-center justify-center">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#181818]">
                {result ? 'Invite sent' : 'Invite a team member'}
              </h2>
              <p className="text-xs text-[#686E6C] mt-0.5">
                {result
                  ? 'They will receive an email with a setup link.'
                  : 'We’ll email them a secure setup link.'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-[#F5F4F2] text-[#686E6C]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!result ? (
          <div className="px-6 py-5 space-y-5">
            <div>
              <label className="block text-xs font-semibold text-[#181818] mb-1.5">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#686E6C]" />
                <input
                  type="email"
                  inputMode="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canSubmit) submit();
                  }}
                  placeholder="colleague@company.com"
                  className="w-full h-11 pl-9 pr-3 rounded-lg border border-[#E6E6E4] bg-white text-[#181818] placeholder:text-[#9A9995] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#181818] mb-1.5">
                Role
              </label>
              <div className="grid grid-cols-1 gap-2">
                {(['ENGINEER', 'OFFICE', 'ADMIN'] as Role[]).map((r) => {
                  const meta = ROLE_META[r];
                  const active = role === r;
                  return (
                    <button
                      type="button"
                      key={r}
                      onClick={() => setRole(r)}
                      className={
                        'w-full text-left px-4 py-3 rounded-lg border transition-all ' +
                        (active
                          ? 'border-orange-500 bg-orange-50/60 ring-2 ring-orange-100'
                          : 'border-[#E6E6E4] hover:border-[#D0CFCB]')
                      }
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-[#181818]">
                          {meta.label}
                        </div>
                        {active && (
                          <CheckCircle2 className="h-4 w-4 text-orange-500" />
                        )}
                      </div>
                      <div className="text-xs text-[#686E6C] mt-0.5">
                        {meta.blurb}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-medium text-[#181818] hover:bg-[#F5F4F2]"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                onClick={submit}
                disabled={!canSubmit}
              >
                <Send className="h-4 w-4" />
                {submitting ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            <div className="rounded-xl border border-[#E6E6E4] bg-gradient-to-br from-orange-50/80 to-white p-4">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#181818]">
                    Invite sent to {result.email}
                  </div>
                  <div className="text-xs text-[#686E6C] mt-1">
                    {result.emailDelivered
                      ? 'They should see it in their inbox within a minute. If not, send them the link below.'
                      : "We couldn't deliver the email automatically. Share the link below directly with them."}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#181818] mb-1.5">
                Setup link
              </label>
              <div className="flex items-stretch gap-2">
                <input
                  readOnly
                  value={result.acceptUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 h-11 px-3 rounded-lg border border-[#E6E6E4] bg-[#F8F8F7] text-xs font-mono text-[#181818] focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex items-center gap-1.5 px-3 rounded-lg border border-[#E6E6E4] bg-white text-sm font-medium text-[#181818] hover:bg-[#F5F4F2]"
                >
                  <Copy className="h-4 w-4" />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="text-[11px] text-[#686E6C] mt-1.5">
                Link expires in 7 days. You can always resend from the pending invites list.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-medium text-[#181818] hover:bg-[#F5F4F2]"
                onClick={inviteAnother}
              >
                Invite another
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 shadow-sm"
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteMemberModal;
